import React, { useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import { X } from 'lucide-react';
import Switch from './custom/Switch'; // Import the Switch component

interface CookiePreferencesProps {
  isOpen: boolean;
  onClose: () => void;
}

const CookiePreferences: React.FC<CookiePreferencesProps> = ({ isOpen, onClose }) => {
  const [preferences, setPreferences] = useState({
    necessary: true, // Always active
    preference: Cookies.get('cookie-preference') === 'true',
    marketing: Cookies.get('cookie-marketing') === 'true',
    statistics: Cookies.get('cookie-statistics') === 'true',
  });

  // Handle individual cookie category toggle
  const handleToggle = (category: 'preference' | 'marketing' | 'statistics', value: boolean) => {
    setPreferences(prev => ({
      ...prev,
      [category]: value
    }));
  };

  // Handle "Allow All" button
  const handleAllowAll = () => {
    setPreferences({
      necessary: true,
      preference: true,
      marketing: true,
      statistics: true
    });
    savePreferences({
      necessary: true,
      preference: true,
      marketing: true,
      statistics: true
    });
    onClose();
  };

  // Handle "Deny All" button (except necessary)
  const handleDenyAll = () => {
    const newPrefs = {
      necessary: true,
      preference: false,
      marketing: false,
      statistics: false
    };
    setPreferences(newPrefs);
    savePreferences(newPrefs);
    onClose();
  };

  // Save preferences to cookies
  const savePreferences = (prefs: typeof preferences) => {
    // Set consent cookie for 1 year
    Cookies.set('cookie-consent', 'custom', { expires: 1 });
    
    // Set individual category cookies
    if (prefs.preference) {
      Cookies.set('cookie-preference', 'true', { expires: 1 });
    } else {
      Cookies.remove('cookie-preference');
    }
    
    if (prefs.marketing) {
      Cookies.set('cookie-marketing', 'true', { expires: 1 });
    } else {
      Cookies.remove('cookie-marketing');
    }
    
    if (prefs.statistics) {
      Cookies.set('cookie-statistics', 'true', { expires: 1 });
    } else {
      Cookies.remove('cookie-statistics');
    }
  };

  // Save preferences when component unmounts
  useEffect(() => {
    return () => {
      if (isOpen) {
        savePreferences(preferences);
      }
    };
  }, [isOpen, preferences]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold text-gray-800">Plus d'informations sur les cookies et la confidentialité</h2>
            <button 
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <X size={24} />
            </button>
          </div>
          
          <p className="text-gray-700 mb-6">
            En cliquant sur "Autoriser tout", vous acceptez le stockage de cookies sur votre appareil pour des
            fins de personnalisation de contenu et d'analyse du trafic.
            Vous pouvez modifier vos paramètres de cookies.
          </p>
          
          <div className="space-y-4">
            <h3 className="font-semibold text-lg text-gray-800">Gérer les groupes de cookies</h3>
            
            {/* Strictly necessary cookies */}
            <div className="bg-gray-50 p-4 rounded-lg flex justify-between items-center">
              <div className="flex items-center gap-2">
                <span className="text-xl">+</span>
                <span className="text-gray-800 font-semibold">Cookies strictement nécessaires</span>
              </div>
              <div className="text-blue-600 font-medium">Toujours actif</div>
            </div>
            
            {/* Preference cookies */}
            <div className="bg-gray-50 p-4 rounded-lg flex justify-between items-center">
              <div className="flex items-center gap-2">
                <span className="text-xl">+</span>
                <span className="text-gray-800 font-semibold">Cookies de préférence</span>
              </div>
              <Switch 
                checked={preferences.preference} 
                onChange={(e) => handleToggle('preference', e.target.checked)} 
              />
            </div>
            
            {/* Marketing cookies */}
            <div className="bg-gray-50 p-4 rounded-lg flex justify-between items-center">
              <div className="flex items-center gap-2">
                <span className="text-xl">+</span>
                <span className="text-gray-800 font-semibold">Cookies de marketing</span>
              </div>
              <Switch 
                checked={preferences.marketing} 
                onChange={(e) => handleToggle('marketing', e.target.checked)} 
              />
            </div>
            
            {/* Statistics cookies */}
            <div className="bg-gray-50 p-4 rounded-lg flex justify-between items-center">
              <div className="flex items-center gap-2">
                <span className="text-xl">+</span>
                <span className="text-gray-800 font-semibold">Cookies de statistiques</span>
              </div>
              <Switch 
                checked={preferences.statistics} 
                onChange={(e) => handleToggle('statistics', e.target.checked)} 
              />
            </div>
          </div>
          
          <div className="flex justify-end gap-4 mt-8">
            <button 
              onClick={handleDenyAll}
              className="bg-gradient-to-r from-blue-400 to-blue-600 hover:from-blue-800 hover:to-blue-950 text-white px-6 py-2 rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
            >
              REFUSER TOUT
            </button>
            <button 
              onClick={handleAllowAll}
              className="bg-gradient-to-r from-blue-400 to-blue-600 hover:from-blue-800 hover:to-blue-950 text-white px-6 py-2 rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
            >
              AUTORISER TOUT
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookiePreferences;